import { Box, Typography, Tabs, Tab, CircularProgress } from '@mui/material';
import React, { ReactElement, JSX, useState, useMemo } from 'react';
import ProjectActivityDetails from '../details/ProjectActivityDetails';
import ProjectDetails from '../details/ProjectDetails';
import ShippingDetails from '../details/shipping/ShippingDetails';
import CommunicationDetails from '../details/CommunicationDetails';
import { SimulationDatasetDetails } from '../details/SimulationDatasetDetails';
import { TheoryActivityDetails } from '../details/TheoryActivityDetails';
import RecipeActivityDetail from '../details/RecipeActivityDetail';
import DataPackageDetails from '../details/DataPackageDetails';
import SampleDetailsReadOnly from '../details/SampleDetailsReadOnly';
import ProjectDetailsReadOnly from '../details/ProjectDetailsReadOnly';
import { log } from '@/lib/utils/logger';
import { useBackend } from '@/hooks/useBackend';
import { LoadingBackdrop } from '@/components/common/LoadingBackdrop';

// Material-UI Icons

import CharacterizationActivityDetails from '@/components/details/CharacterizationActivityDetails';
import TransferActivity from '@/components/details/TransferActivity';
import SplitActivity from '@/components/details/SplitActivity';
import StorageActivity from '@/components/details/StorageActivity';
import SampleDetails from '@/components/details/SampleDetails';
import { NotesDetails } from '@/components/details/NotesDetails';
import { FilesDetails } from '@/components/details/FilesDetails';
import InventoryActivity from '@/components/details/InventoryActivity';
import { ActivityCategoryDto, ActivityTypeDto, PrivilegeEnum } from '@/api/ApiClient';
import { getActivityTypeDisplay } from '@/lib/types/activityTypes';
import { useTree } from '@/hooks/useTree';
import { nodeTypeSupportsFiles, nodeTypeSupportsNotes, nodeIsReadOnlyForUi } from '@/lib/tree/treeNodeTypeConfigs';
import { useDataActions, useDataState } from '@/hooks/useDataState';
import { TreeNode } from '@/types/tree/node';
import { NodeType } from '@/types/tree/base';
import { usePrivileges } from '@/hooks/usePrivileges';

import ProjectSampleRequestsTab from '@/components/details/ProjectSampleRequestsTab';
import PublicationDetails from '@/components/details/PublicationDetails';

/**
 * DetailArea component displays detailed information about the selected sample or activity
 * in the sample tree. It shows different information based on whether a sample or activity
 * is selected.
 *
 * @component
 * @returns {JSX.Element | null} The rendered DetailArea component or null if no node is selected
 */
export const DetailAreaDispatcher = ({
  selectedNode,
  openSampleRequestsTab,
}: {
  selectedNode: TreeNode<NodeType> | null;
  openSampleRequestsTab?: boolean;
}): ReactElement | null => {
  const { loadFiles, loadNotes, getSampleActivityData, getNotes, getFiles, getProjectData } = useTree();

  // Sample requests belong to editable project workspaces; `/data` browse roots use `projectInData`.
  const isProjectForSampleRequests = selectedNode?.type === 'project';
  const isProjectNode = selectedNode?.type === 'project' || selectedNode?.type === 'projectInData';
  const project = isProjectNode && selectedNode ? getProjectData(selectedNode) : undefined;
  const projectId = project?.id?.toString();

  // Use project-specific privileges for project and browse-only project roots (notes, etc.)
  const { isAuthorized } = usePrivileges(isProjectNode ? 'project' : undefined, projectId);

  const isReadOnlyNode = selectedNode ? nodeIsReadOnlyForUi(selectedNode.type) : false;

  // Get notes and files for readonly nodes to check if tabs should be shown
  const notes = selectedNode ? (getNotes(selectedNode.dataId) ?? []) : [];
  const files = selectedNode ? (getFiles(selectedNode.dataId) ?? []) : [];

  // Only show tabs if the node type supports them (from nodeTypeConfig)
  const supportsNotes = selectedNode ? nodeTypeSupportsNotes(selectedNode.type) : false;
  const supportsFiles = selectedNode ? nodeTypeSupportsFiles(selectedNode.type) : false;

  // For readonly nodes, only show tabs if there's content to display
  const shouldShowNotesTab = supportsNotes && (!isReadOnlyNode || notes.length > 0);
  const shouldShowFilesTab = supportsFiles && (!isReadOnlyNode || files.length > 0);
  const shouldShowSampleRequestsTab = isProjectForSampleRequests && isAuthorized(PrivilegeEnum.SampleRequest, 'read');

  // Build a map of available tabs and their indices by building tabs in order
  const tabMap = useMemo(() => {
    const availableTabs: string[] = ['details'];
    if (shouldShowNotesTab) availableTabs.push('notes');
    if (shouldShowFilesTab) availableTabs.push('files');
    if (shouldShowSampleRequestsTab) availableTabs.push('sampleRequests');

    const map: Record<string, number> = {};
    availableTabs.forEach((tab, index) => {
      map[tab] = index;
    });
    return map;
  }, [shouldShowNotesTab, shouldShowFilesTab, shouldShowSampleRequestsTab]);

  const { notesLoaded, filesLoaded } = useDataState(selectedNode?.dataId);
  const backend = useBackend();

  const shouldOpenSampleRequestsTab =
    openSampleRequestsTab && shouldShowSampleRequestsTab && tabMap.sampleRequests !== undefined;
  const initialTabIndex = shouldOpenSampleRequestsTab ? tabMap.sampleRequests : 0;
  const [tabIndex, setTabIndex] = useState(initialTabIndex);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);

    // Load notes when user switches to notes tab
    if (newValue === tabMap.notes && selectedNode && nodeTypeSupportsNotes(selectedNode.type) && !notesLoaded) {
      if (backend) {
        loadNotes(selectedNode.dataId, backend).catch((error) => {
          log.error('[DetailAreaDispatcher] Error loading notes when switching to notes tab:', error);
        });
      }
    }

    // Load files when user switches to files tab
    if (newValue === tabMap.files && selectedNode && nodeTypeSupportsFiles(selectedNode.type) && !filesLoaded) {
      if (backend) {
        loadFiles(selectedNode.dataId, backend).catch((error) => {
          log.error('[DetailAreaDispatcher] Error loading files when switching to files tab:', error);
        });
      }
    }
  };

  // Subscribe to specific loading states for better performance
  const { isLoadingFiles, isDeleting, isLoadingNotes } = useDataActions(selectedNode?.dataId);

  // Get the appropriate tab label based on node type
  const getTabLabel = (): string => {
    if (!selectedNode) return 'Details';

    // For sample activities, use the activity type display name
    if (
      selectedNode.type === 'sampleActivity' ||
      selectedNode.type === 'sampleActivityInPackage' ||
      selectedNode.type === 'sampleActivitySnapshot' ||
      selectedNode.type === 'sampleActivityAggregate'
    ) {
      const sampleActivityData = getSampleActivityData(selectedNode);
      if (sampleActivityData) {
        const activityTypeDisplay = getActivityTypeDisplay(sampleActivityData.type);
        if (activityTypeDisplay) {
          return `${activityTypeDisplay} Details`;
        }
        // Fallback to category if type not found
        const categoryDisplay = sampleActivityData.category
          ? sampleActivityData.category.charAt(0) + sampleActivityData.category.slice(1).toLowerCase()
          : 'Activity';
        return `${categoryDisplay} Details`;
      }
    }

    // For other node types, use a capitalized version of the type
    const typeMap: Record<string, string> = {
      sample: 'Sample',
      sampleAggregate: 'Sample',
      sampleInPackage: 'Sample',
      sampleSnapshot: 'Sample',
      project: 'Project',
      projectInData: 'Project',
      projectActivity: 'Project Activity',
      publication: 'Publication',
      publicationInData: 'Publication',
      shipping: 'Shipping',
      communication: 'Communication',
      simulationDataset: 'Simulation Dataset',
      theoryActivity: 'Theory Activity',
      theoryActivityInPackage: 'Theory Activity',
      dataPackage: 'Data Package',
    };

    return `${typeMap[selectedNode.type] || 'Details'} Details`;
  };

  if (!selectedNode) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Loading node data...
        </Typography>
      </Box>
    );
  }

  const renderSampleActivity = (): JSX.Element | null => {
    const sampleActivityData = getSampleActivityData(selectedNode);
    if (!sampleActivityData) {
      if (
        selectedNode.type !== 'sampleActivity' &&
        selectedNode.type !== 'sampleActivityInPackage' &&
        selectedNode.type !== 'sampleActivitySnapshot' &&
        selectedNode.type !== 'sampleActivityAggregate'
      ) {
        log.error('Wonder how we got here, that does not look like sample activity', () => {
          selectedNode;
        });
      }
      return null;
    }

    log.debug(`[DetailAreaDispatcher] Got sample activity data`, () => ({
      category: sampleActivityData.category,
      type: sampleActivityData.type,
      hasRecipe: !!sampleActivityData.recipe,
    }));

    switch (sampleActivityData.category) {
      case ActivityCategoryDto.Characterization:
        return <CharacterizationActivityDetails />;
      case ActivityCategoryDto.Transfer:
        return <TransferActivity />;
      case ActivityCategoryDto.Inventory:
        return <InventoryActivity />;
      case ActivityCategoryDto.Storage:
        return <StorageActivity />;
      case ActivityCategoryDto.Processing:
        switch (sampleActivityData.type) {
          case ActivityTypeDto.Synthesis:
          case ActivityTypeDto.Preparation:
          case ActivityTypeDto.PostTreatment: {
            return <RecipeActivityDetail />;
          }
          case ActivityTypeDto.Dicing:
            return <SplitActivity />;
          default:
            log.error('[DetailAreaDispatcher] Unknown PROC activity type', sampleActivityData.type);
            return null;
        }
      default:
        log.error('[DetailAreaDispatcher] Unknown activity category', sampleActivityData.category);
        return null;
    }
  };

  const getContent = (): JSX.Element | null => {
    if (!selectedNode) return null;

    switch (selectedNode.type) {
      case 'sample':
        return <SampleDetails />;
      case 'sampleAggregate':
      case 'sampleInPackage':
      case 'sampleSnapshot':
        return <SampleDetailsReadOnly />;
      case 'projectActivity':
        return <ProjectActivityDetails />;
      case 'publication':
      case 'publicationInData':
        return <PublicationDetails />;
      case 'project':
        return <ProjectDetails />;
      case 'projectInData':
        return <ProjectDetailsReadOnly />;
      case 'shipping':
        return <ShippingDetails />;
      case 'communication':
        return <CommunicationDetails />;
      case 'simulationDataset':
        return <SimulationDatasetDetails />;
      case 'theoryActivity':
      case 'theoryActivityInPackage':
        return <TheoryActivityDetails />;
      case 'dataPackage':
        return <DataPackageDetails />;
      case 'sampleActivity':
      case 'sampleActivityInPackage':
      case 'sampleActivitySnapshot':
      case 'sampleActivityAggregate':
        return renderSampleActivity();
      default:
        return <div>Unknown node type</div>;
    }
  };

  const detailContent = (
    <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
      {!selectedNode ? (
        <Typography variant="body1" color="text.secondary">
          Select a node to view details
        </Typography>
      ) : (
        <>
          <Tabs value={tabIndex} onChange={handleTabChange} aria-label="Detail tabs" sx={{ flexShrink: 0 }}>
            <Tab label={getTabLabel()} />
            {shouldShowNotesTab ? <Tab label="Notes" /> : null}
            {shouldShowFilesTab ? <Tab label="Files" /> : null}
            {shouldShowSampleRequestsTab ? <Tab label="Sample Requests" /> : null}
          </Tabs>
          <Box sx={{ mt: 2, flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Details tab: all detail components have footers and manage their own scroll */}
            {tabIndex === tabMap.details ? (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                {getContent()}
              </Box>
            ) : null}

            {/* Notes tab: dispatcher provides scrolling container */}
            {tabIndex === tabMap.notes ? (
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                {isLoadingNotes ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <NotesDetails notes={selectedNode ? (getNotes(selectedNode.dataId) ?? []) : []} node={selectedNode} />
                )}
              </Box>
            ) : null}

            {/* Files tab: dispatcher provides scrolling container */}
            {tabIndex === tabMap.files ? (
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                {isLoadingFiles ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <FilesDetails files={selectedNode ? (getFiles(selectedNode.dataId) ?? []) : []} node={selectedNode} />
                )}
              </Box>
            ) : null}

            {/* Sample Requests tab: dispatcher provides scrolling container */}
            {tabIndex === tabMap.sampleRequests && project ? (
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <ProjectSampleRequestsTab
                  projectId={project.id!}
                  projectLabel={project.projectLabel || ''}
                  isReadOnly={isReadOnlyNode}
                  projectEndDate={project.endDate}
                />
              </Box>
            ) : null}
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box
      sx={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      data-testid="detail-area"
    >
      {detailContent}
      <LoadingBackdrop open={isDeleting} title="Deleting..." subtitle="Please wait while the item is being deleted." />
    </Box>
  );
};
